/**
 * <caos-reveal>: reveals its children once, when the element scrolls into view.
 *
 * Progressive enhancement: nothing is hidden until this component runs.
 * Content already in the viewport on load is never hidden (protects LCP and CLS).
 * Skipped with prefers-reduced-motion, in the theme editor, and without IntersectionObserver.
 *
 * Children opt in with [data-caos-reveal-item] (fade + rise) or [data-caos-reveal-media] (clip reveal).
 * Stagger order comes from the --caos-reveal-index custom property set in Liquid.
 */
class CaosReveal extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    if (window.Shopify?.designMode) return;
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const { top } = this.getBoundingClientRect();
    if (top < window.innerHeight) return;

    this.setAttribute('data-state', 'pending');

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.setAttribute('data-state', 'revealed');
        this.#observer?.disconnect();
      },
      { rootMargin: '0px 0px -15% 0px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('caos-reveal')) {
  customElements.define('caos-reveal', CaosReveal);
}
